import type { MeasuredBlocks, PrintDocumentModel } from './types';

export const PRINT_ROW_ATTR = 'data-print-row';
export const PRINT_SECTION_ATTR = 'data-print-section';
export const PRINT_THEAD_ATTR = 'data-print-thead';

const CONTINUATION_ROW_CHROME_PX = 18;

function outerHeight(el: Element): number {
  const rect = el.getBoundingClientRect();
  const style = window.getComputedStyle(el);
  return rect.height + (parseFloat(style.marginTop) || 0) + (parseFloat(style.marginBottom) || 0);
}

/**
 * Replace text-length estimates with heights read from an already-rendered plan. Rows clipped by a
 * page's overflow still have their real height, so one estimated render is enough to re-plan.
 */
export function measureRenderedPrintBlocks(
  root: HTMLElement,
  model: PrintDocumentModel,
  estimated: MeasuredBlocks
): MeasuredBlocks {
  const rowSegments = new Map<string, number[]>();
  root.querySelectorAll(`[${PRINT_ROW_ATTR}]`).forEach((el) => {
    const key = el.getAttribute(PRINT_ROW_ATTR) || '';
    const list = rowSegments.get(key) || [];
    list.push(el.getBoundingClientRect().height);
    rowSegments.set(key, list);
  });

  const sectionHeights = new Map<string, number>();
  const sectionPage = new Map<string, string>();
  root.querySelectorAll(`[${PRINT_SECTION_ATTR}]`).forEach((el) => {
    const id = el.getAttribute(PRINT_SECTION_ATTR) || '';
    const page = el.closest('[data-page]')?.getAttribute('data-page') ?? '';
    if (!sectionPage.has(id)) sectionPage.set(id, page);
    if (sectionPage.get(id) !== page) return;
    sectionHeights.set(id, (sectionHeights.get(id) ?? 0) + outerHeight(el));
  });

  const thead = root.querySelector(`[${PRINT_THEAD_ATTR}]`);

  const itemFull = estimated.itemFull.map((row) => [...row]);
  const itemChunks = estimated.itemChunks.map((t) => t.map((c) => [...c]));
  model.tables.forEach((table, t) => {
    table.rows.forEach((_, i) => {
      const segs = rowSegments.get(`${t}:${i}`);
      if (!segs?.length || segs.some((h) => h <= 0)) return;
      const chrome = estimated.itemRowChrome[t]?.[i] ?? 22;
      const full = segs.reduce((s, h) => s + h, 0) - (segs.length - 1) * CONTINUATION_ROW_CHROME_PX;
      const estFull = estimated.itemFull[t]?.[i] ?? full;
      itemFull[t][i] = Math.max(full, 1);
      const estDesc = Math.max(1, estFull - chrome);
      const ratio = Math.max(0.25, (full - chrome) / estDesc);
      itemChunks[t][i] = (estimated.itemChunks[t]?.[i] ?? [full]).map((h) => h * ratio);
    });
  });

  const pick = (record: Record<string, number>) => {
    const out: Record<string, number> = { ...record };
    for (const id of Object.keys(out)) {
      const h = sectionHeights.get(id);
      if (h != null && h > 0) out[id] = h;
    }
    return out;
  };

  return {
    ...estimated,
    firstSections: pick(estimated.firstSections),
    finalSections: pick(estimated.finalSections),
    tableHeader: thead?.getBoundingClientRect().height || estimated.tableHeader,
    itemFull,
    itemChunks,
  };
}
